import { AlertTriangle, Shield, ShieldCheck } from "lucide-react";

const C = {
  surface: "var(--bc-panel, #18181B)", highlight: "var(--bc-panel-elev, #27272A)",
  text: "var(--bc-ink, #FAFAFA)", muted: "var(--bc-sub, #A1A1AA)",
  green: "#22C55E", yellow: "#F59E0B", red: "#EF4444",
};

function SignificantChangesBanner({ changes }) {
  if (!changes || !changes.has_changes) return null;
  const color = changes.red_players?.length > 0 ? C.red : C.yellow;
  return (
    <div
      data-testid="significant-changes-banner"
      className="flex items-start gap-3 p-4"
      style={{
        background: `${color}10`,
        border: `1px solid ${color}35`,
        borderLeft: `3px solid ${color}`,
      }}
    >
      <AlertTriangle size={16} style={{ color, flexShrink: 0, marginTop: 2 }} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span
            className="font-heading font-black text-xs uppercase tracking-[0.2em]"
            style={{ color }}
          >
            ATTENZIONE · CAMBIAMENTI RECENTI
          </span>
          <span
            className="font-data text-[10px] px-2 py-0.5 uppercase tracking-widest"
            style={{
              color,
              background: `${color}18`,
              border: `1px solid ${color}35`,
            }}
          >
            Ricontrolla prima di scommettere
          </span>
        </div>
        <p
          className="font-data text-xs leading-relaxed"
          style={{ color: C.text }}
        >
          {changes.summary || "Novità dell'ultima ora potrebbero alterare le probabilità."}
        </p>
        {changes.red_players?.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {changes.red_players.slice(0, 4).map((rp, i) => (
              <span
                key={i}
                className="font-data text-[10px] px-2 py-0.5 uppercase tracking-wider"
                style={{
                  color: C.red,
                  background: "rgba(239,68,68,0.12)",
                  border: "1px solid rgba(239,68,68,0.3)",
                }}
              >
                {rp.name}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function HotKeeperChip({ keeper, teamName, side }) {
  if (!keeper?.hot) return null;
  return (
    <div
      data-testid={`hot-keeper-${side}`}
      className="flex items-start gap-2 p-3"
      style={{
        background: "rgba(34,197,94,0.06)",
        border: `1px solid rgba(34,197,94,0.25)`,
      }}
    >
      <ShieldCheck size={13} style={{ color: C.green, flexShrink: 0, marginTop: 1 }} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span
            className="font-heading font-black text-[10px] uppercase tracking-widest"
            style={{ color: C.green }}
          >
            PORTIERE IN FORMA
          </span>
          <span
            className="font-data text-[9px] uppercase tracking-wider"
            style={{ color: C.muted }}
          >
            · {teamName}
          </span>
        </div>
        <p
          className="font-data text-[10px] leading-snug"
          style={{ color: C.text }}
        >
          {keeper.reason}
        </p>
      </div>
    </div>
  );
}

export default function MatchFlagsPanel({ flags, homeName, awayName }) {
  if (!flags) return null;
  const hasHotHome = flags.hot_keeper_home?.hot;
  const hasHotAway = flags.hot_keeper_away?.hot;
  const hasChanges = flags.significant_changes?.has_changes;
  if (!hasHotHome && !hasHotAway && !hasChanges) return null;

  return (
    <div
      data-testid="match-flags-panel"
      style={{
        background: C.surface,
        border: `1px solid ${C.highlight}`,
        borderTop: `2px solid ${C.yellow}`,
      }}
    >
      <div
        className="px-4 pt-3 pb-2 flex items-center gap-2"
        style={{ borderBottom: `1px solid ${C.highlight}` }}
      >
        <Shield size={13} style={{ color: C.yellow }} />
        <span
          className="font-heading font-black text-xs uppercase tracking-[0.2em]"
          style={{ color: C.yellow }}
        >
          SEGNALI DA CONSIDERARE
        </span>
        <span
          className="font-data text-[10px] uppercase tracking-widest ml-auto"
          style={{ color: C.muted }}
        >
          · letti fuori dal modello
        </span>
      </div>
      <div className="p-3 space-y-2">
        <SignificantChangesBanner changes={flags.significant_changes} />
        {hasHotHome && (
          <HotKeeperChip
            keeper={flags.hot_keeper_home}
            teamName={homeName}
            side="home"
          />
        )}
        {hasHotAway && (
          <HotKeeperChip
            keeper={flags.hot_keeper_away}
            teamName={awayName}
            side="away"
          />
        )}
      </div>
    </div>
  );
}
